import React, { useState } from 'react';
import {
  RocketLaunch as RocketIcon,
  Search as SearchIcon,
  ThumbUp as ThumbUpIcon,
  TrendingUp as TrendingIcon,
  Email as EmailIcon,
  Chat as ChatIcon,
  Groups as GroupsIcon,
  BarChart as AnalyticsIcon,
  Receipt as ReceiptIcon,
  Bolt as BoltIcon,
  ArrowRight as ArrowRightIcon,
  CheckCircle as CheckCircleIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

const DigitalMarketingPage = () => {
  const theme = useTheme();
  const [hoveredCard, setHoveredCard] = useState(null);
  const navigate = useNavigate();

  const services = [
    {
      icon: <SearchIcon fontSize="large" />,
      title: "SEO (Search Engine Optimization)",
      description: "Boost your website's visibility and organic traffic with our proven SEO strategies that align with the latest search engine algorithms.",
      image: "https://oneclickwi.com/wp-content/uploads/2023/10/Depositphotos_36633383_L-1030x729.jpg"
    },
    {
      icon: <GroupsIcon fontSize="large" />,
      title: "Social Media Marketing",
      description: "Engage your audience and build brand loyalty through strategic social media campaigns across all major platforms.",
      image: "https://itqubes.lk/wp-content/uploads/2025/05/digital-marketing-illustration-design_260839-10.avif"
    },
    {
      icon: <TrendingIcon fontSize="large" />,
      title: "SEM / PPC Advertising",
      description: "Get immediate visibility and qualified leads with our data-driven pay-per-click campaigns that maximize your advertising ROI.",
      image: "https://cityinnovates.com/wp-content/uploads/2022/02/sem.jpg"
    },
    {
      icon: <ReceiptIcon fontSize="large" />,
      title: "Local SEO & Business Listings",
      description: "Dominate local search results and Google My Business listings to drive more foot traffic and local customers.",
      image: "https://daltonluka.com/wp-content/uploads/2020/06/local-citations-991x496.webp"
    },
    {
      icon: <EmailIcon fontSize="large" />,
      title: "Email Marketing",
      description: "Nurture leads and retain customers with personalized email campaigns that convert and build lasting relationships.",
      image: "https://timesinternet.in/blog/wp-content/uploads/2023/12/Shutterstock_1814591309.jpg"
    },
    {
      icon: <ChatIcon fontSize="large" />,
      title: "WhatsApp Marketing",
      description: "Leverage the power of WhatsApp for personalized customer engagement, promotions, and instant communication.",
      image: "https://i0.wp.com/blog.velocity.in/wp-content/uploads/2024/02/Velocity-Pay-Blog-7.png?w=1862&ssl=1"
    }
  ];

  const benefits = [
    { icon: <AnalyticsIcon />, title: "Data-Driven", description: "Decisions based on analytics & result" },
    { icon: <BoltIcon />, title: "Quick Results", description: "Visible impact in shorter timeframes" },
    { icon: <CheckCircleIcon />, title: "Proven Methods", description: "Tested techniques that actually work" },
    { icon: <ReceiptIcon />, title: "Transparent", description: "Clear metrics and performance updates" }
  ];

  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box sx={{
        position: 'relative',
        bgcolor: 'rgba(63, 81, 181, 0.9)',
        color: 'primary.contrastText',
        py: 12,
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
          zIndex: 1
        }
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Stack alignItems="center" textAlign="center" spacing={4}>
            <Chip
            onClick={()=>console.log("")}
              icon={<RocketIcon />}
              label="Your Partner in Digital Marketing Excellence"
              sx={{
                bgcolor: 'rgba(208, 227, 239, 0.55)',
                color: 'primary.contrastText',
                px: 2,
                py: 1,
                fontSize: '0.875rem'
              }}
            />

            <Typography variant="h2" component="h1" sx={{ fontWeight: 700, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
              Unlock Your <Box component="span" sx={{ color: '#61dafb' }}>Digital Potential</Box>
            </Typography>

            <Typography variant="h5" sx={{ maxWidth: 800, opacity: 0.9 }}>
              Are you ready to unlock the full potential of your online presence? Look no further!
              ShreeshTech is your trusted partner in the world of Digital Marketing.
              Welcome to a future where your brand's success knows no bounds.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                endIcon={<ArrowRightIcon />}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  '&:hover': {
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => navigate('/get-in-touch')}
              >
                Get Started
              </Button>

              <Button
                variant="outlined"
                color="inherit"
                size="large"
                onClick={() => navigate('/case-studies')}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  borderColor: alpha(theme.palette.common.white, 0.3),
                  color: 'primary.contrastText',
                  '&:hover': {
                    borderColor: theme.palette.common.white,
                    bgcolor: alpha(theme.palette.common.white, 0.1)
                  }
                }}
              >
                View Case Studies
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Expertise Section - Now in single row */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
            <Typography variant="h3" component="h2" sx={{ fontWeight: 700, mb: 3 }}>
              Our Digital Marketing Expertise
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              At ShreeshTech, we understand the dynamic landscape of digital marketing like no other.
              With years of experience and a passion for innovation, we've helped countless businesses
              achieve their online goals.
            </Typography>
            <List dense>
              {[
                "360° digital marketing strategies",
                "Transparent reporting and analytics",
                "Custom solutions for your business",
                "Continuous optimization for best results",
                "Dedicated account managers"
              ].map((item, index) => (
                <ListItem key={index} disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
            <Box
              component="img"
              src="https://plus.unsplash.com/premium_photo-1684225764999-3597a8da10ab?q=80&w=3432&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              alt="Digital Marketing"
              sx={{
                width: '100%',
                borderRadius: 2,
                boxShadow: theme.shadows[4],
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.02)'
                }
              }}
            />
          </Grid>
        </Grid>
      </Container>


      <Box sx={{ bgcolor: 'grey.50', py: 10 }}>
        <Stack spacing={6} alignItems="center" textAlign="center">
          <Typography
            variant="h3"
            component="h2"
            sx={{
              fontWeight: 800,
              color: 'primary.main',
              fontSize: { xs: '2rem', md: '2.75rem' },
            }}
          >
            Why Choose ShreeshTech?
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {benefits.map((benefit, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    width: '100%',
                    maxWidth: 280, // Ensures same width
                    minHeight: 260,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    borderRadius: 3,
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: theme => theme.shadows[6],
                      bgcolor: theme => alpha(theme.palette.primary.light, 0.05),
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 32,
                      mb: 2,
                    }}
                  >
                    {benefit.icon}
                  </Box>

                  <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Box>

      {/* Services Section - Improved Alignment */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Stack spacing={{ xs: 4, md: 6 }} alignItems="center" textAlign="center">
          <Typography variant="h3" component="h2" sx={{
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '3rem' }
          }}>
            Our Comprehensive Digital Marketing Services
          </Typography>

          <Typography variant="h6" color="text.secondary" sx={{
            maxWidth: 700,
            px: { xs: 2, md: 0 },
            fontSize: { xs: '1rem', md: '1.25rem' }
          }}>
            We offer a full spectrum of digital marketing services to help your business grow online
          </Typography>

          <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index} sx={{
                display: 'flex',
                justifyContent: 'center'
              }}>
                <Card
                  elevation={hoveredCard === index ? 6 : 2}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{
                    width: '100%',
                    maxWidth: 380,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)'
                    }
                  }}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={service.image}
                    alt={service.title}
                    sx={{
                      objectFit: 'cover',
                      objectPosition: 'center'
                    }}
                  />
                  <CardContent sx={{
                    p: { xs: 3, md: 4 },
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: 'primary.light',
                        color: 'primary.contrastText',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        mx: 'auto',
                        transition: 'all 0.3s ease',
                        ...(hoveredCard === index && {
                          transform: 'scale(1.1)',
                          bgcolor: 'secondary.main'
                        })
                      }}
                    >
                      {service.icon}
                    </Box>
                    <Typography variant="h5" component="h3" gutterBottom sx={{
                      fontSize: { xs: '1.25rem', md: '1.5rem' }
                    }}>
                      {service.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{
                      fontSize: { xs: '0.875rem', md: '1rem' }
                    }}>
                      {service.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Container>

      {/* Social Proof Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 10 }}>
        <Container maxWidth="lg">
          <Stack spacing={4} alignItems="center" textAlign="center">
            <Typography variant="h4" component="h3" sx={{ fontWeight: 700 }}>
              Trusted by Businesses Worldwide
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700 }}>
              Join hundreds of satisfied clients who've transformed their digital presence with us
            </Typography>
            <Stack direction="row" spacing={4} sx={{ mt: 4 }} flexWrap="wrap" justifyContent="center">
              {[FacebookIcon, InstagramIcon, TwitterIcon, LinkedInIcon, WhatsAppIcon].map((Icon, index) => (
                <IconButton
                  key={index}
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: 'white',
                    color: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: theme.palette.primary.light,
                      color: 'white'
                    }
                  }}
                >
                  <Icon fontSize="large" />
                </IconButton>
              ))}
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{
        bgcolor: 'rgba(17, 135, 208, 0.82)',
        color: 'primary.contrastText',
        py: 10,
        backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`
      }}>
        <Container maxWidth="lg">
          <Stack spacing={4} alignItems="center" textAlign="center">
            <Typography variant="h3" component="h2" sx={{ fontWeight: 700 }}>
              Ready to Transform Your Digital Presence?
            </Typography>

            <Typography variant="h6" sx={{ maxWidth: 700, opacity: 0.9 }}>
              Let's discuss your goals and create a winning digital marketing strategy together.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem'
                }}
              >
                Get Free Consultation
              </Button>

              <Button
                variant="outlined"
                color="inherit"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  borderColor: alpha(theme.palette.common.white, 0.3),
                  color: 'primary.contrastText',
                  '&:hover': {
                    borderColor: theme.palette.common.white,
                    bgcolor: 'rgba(17, 135, 208, 0.82)',
                  }
                }}
              >
                Call Us Now
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default DigitalMarketingPage;